const regionProfiles = {
  global: {
    domainSuffix: 'manage.aws.bmw.cloud',
    awsLoginDetails: {
      cdhLoginUrl: 'https://data.bmw.cloud/',
      switchRoleUrl: 'https://signin.aws.amazon.com/',
      loginRegion: 'eu-central-1',
      myBMWItUrl: 'https://bmwgroupprod.service-now.com/mybmwit?id=sc_cat_item&service_id=public_cloud',
      awsPartition: 'aws',
      ssoLoginUrl: 'https://bmw-sso.awsapps.com/start',
      ssoLoginUrlSecure: 'https://bmw-sso-secure.awsapps.com/start',
    },
    currency: 'USD',
    tabPrefix: 'AWS',
    language: 'en-US',
  },
  china: {
    domainSuffix: 'manage.aws-cn.bmw.cloud',
    awsLoginDetails: {
      cdhLoginUrl: 'https://data.bmw.cloud/',
      switchRoleUrl: 'https://signin.amazonaws.cn/',
      loginRegion: 'cn-north-1',
      myBMWItUrl: 'https://bmwgroupprod.service-now.com/sp?id=sc_cat_item&service_id=public_cloud_china',
      awsPartition: 'aws-cn',
      ssoLoginUrl: 'https://start.cn-north-1.home.awsapps.cn/directory/bmwcn-sso',
    },
    currency: 'CNY',
    tabPrefix: 'AWS China',
    language: 'zh-CN',
  },
};

const environmentProfiles = {
  'dev|aws': {
    ...regionProfiles.global,
  },
  'int|aws': {
    ...regionProfiles.global,
  },
  'prod|aws': {
    backendEnv: 'prod',
    ...regionProfiles.global,
  },
  'int|aws-cn': {
    ...regionProfiles.china,
  },
  'prod|aws-cn': {
    backendEnv: 'prod',
    ...regionProfiles.china,
  },
  localhost: {
    // Note: process.env.STAGE_NAME gets injected at build time
    backendEnv: process.env.STAGE_NAME,
    apiDomainPrefix: `${process.env.STAGE_NAME}.`,
    redirectUrl: 'http://localhost:8080/',
    // Use local proxy for Alpha OIDC to avoid CORS issues
    useLocalAlphaProxy: true,
    ...regionProfiles.global,
  },
};

const hostnameRegex = /^(?:(?<subdomain>[a-z_-]+)\.)?manage\.(?<region>aws|aws-cn)\.bmw\.cloud$/;

function getEnvProfileForUrl(url) {
  const match = url.hostname.match(hostnameRegex);
  if (match) {
    const { subdomain, region } = match.groups;
    const environments = Object.keys(environmentProfiles);
    const key = `${subdomain ?? 'prod'}|${region}`;
    const envProfile =
      environments.indexOf(key) === -1 ? environmentProfiles[`dev|${region}`] : environmentProfiles[key];

    return {
      // use default values for backendEnv and apiDomainPrefix if not specified
      backendEnv: subdomain,
      apiDomainPrefix: subdomain ? subdomain + '.' : '',
      ...envProfile,
    };
  } else {
    // if on localhost
    // save stage name in session storage
    // used by microservices on localhost development
    sessionStorage.setItem('stageName', process.env.STAGE_NAME);

    return environmentProfiles.localhost;
  }
}

// check if it is on localhost
// used in microservices loading js
export const checkOnLocalhost = () => {
  const url = new URL(window.location.href);
  const match = url.hostname.match(hostnameRegex);
  return !match;
};

export function getConfigFromUrl(url) {
  if (!url) {
    url = window.location.href;
  }
  if (typeof url === 'string') {
    url = new URL(url);
  }

  const envProfile = getEnvProfileForUrl(url);
  const { apiDomainPrefix, domainSuffix, backendEnv, awsLoginDetails } = envProfile;

  const redirectUrl = envProfile.redirectUrl ?? `${url.protocol}//${url.host}/`;

  // prod/prod-cn use the bare hostname, dev/int/int-cn use a "-<stage>" hostname suffix
  const apimStageSuffix = backendEnv && backendEnv !== 'prod' ? `-${backendEnv}` : '';
  const apimPartition = awsLoginDetails.awsPartition;

  // Alpha OIDC provider configuration (same for all environments)
  const alphaOIDCBaseUrl = 'https://emea.prod.alpha.sso.bmwgroup.com/auth/oauth2/realms/root/realms/alpha';
  const alphaClientId = '7bf58ad3-c2d3-4815-90f9-8a1b2d37fa5b';
  const alphaRedirectUri = `${url.protocol}//${url.host}/oauth2/callback`;

  // Use local proxy for Alpha OIDC on localhost to avoid CORS issues
  const alphaOIDCTokenUrl = envProfile.useLocalAlphaProxy ? '/auth/oauth2/realms/root/realms/alpha' : alphaOIDCBaseUrl;

  // All environments (including prod and prod-cn) use strongAuth7000Service (yubikey/passkey required)
  const acrValues = 'strongAuth7000Service';

  return {
    redirectUrl,
    apiEndpoint: `https://frontend-api.${apiDomainPrefix}${domainSuffix}/v1`,
    apiUserAPIEndpoint: `https://user-api.${apiDomainPrefix}${domainSuffix}/v1`,
    apimAPIExternalEndpoint: `https://api${apimStageSuffix}.bmw.com/fpc/${apimPartition}`,
    apimAPIInternalEndpoint: `https://api${apimStageSuffix}.bmwgroup.net/fpc/${apimPartition}`,
    // Alpha OIDC configuration
    authEndpointAlpha: `${alphaOIDCBaseUrl}/authorize`,
    tokenEndpointAlpha: `${alphaOIDCTokenUrl}/access_token`,
    // Absolute token endpoint for display purposes (tokenEndpointAlpha may be a local-proxy relative path)
    tokenEndpointAlphaAbsolute: `${alphaOIDCBaseUrl}/access_token`,
    tokenRevokeEndpointAlpha: `${alphaOIDCTokenUrl}/token/revoke`,
    logoutEndpointAlpha: `${alphaOIDCBaseUrl}/connect/endSession`,
    jwksUriAlpha: `${alphaOIDCBaseUrl}/connect/jwk_uri`,
    audienceAlpha: alphaClientId,
    clientIdAlpha: alphaClientId,
    redirectUriAlpha: alphaRedirectUri,
    ...envProfile,
    webEAMAuthorizeParamsAlpha: {
      redirect_uri: alphaRedirectUri,
      response_type: 'code',
      client_id: alphaClientId,
      scope: 'openid profile email',
      acr_values: acrValues,
    },
  };
}

export const CONF = getConfigFromUrl();

export function getMicroServicesConfig(url) {
  if (!url) {
    url = window.location.href;
  }
  if (typeof url === 'string') {
    url = new URL(url);
  }

  const envProfile = getEnvProfileForUrl(url);
  const { apiDomainPrefix, domainSuffix } = envProfile;
  const changelogBaseUrl = `https://${apiDomainPrefix}${domainSuffix}/static/micro_service_changelogs`;
  const changelogBaseUrlMicroService = `https://${apiDomainPrefix}${domainSuffix}/micro_services`;

  // register micro-services names
  return {
    'aws-send-mails': {
      url: `${changelogBaseUrl}/_aws_send_mails_changelog.html`,
    },
    'aws-customer-portal-feature-flags-frontend': {
      url: `${changelogBaseUrlMicroService}/aws-customer-portal-feature-flags-frontend/_changelog.html`,
    },
    'aws-customer-portal-fqdn-management': {
      url: `${changelogBaseUrlMicroService}/aws-customer-portal-fqdn-management/_changelog.html`,
    },
    'aws-customer-portal-idc-management': {
      url: `${changelogBaseUrlMicroService}/aws-customer-portal-idc-management/_changelog.html`,
    },
    'aws-customer-portal-scp-changelog-management': {
      url: `${changelogBaseUrlMicroService}/aws-customer-portal-scp-changelog-management/_changelog.html`,
    },
    'aws-customer-portal-search-service': {
      url: `${changelogBaseUrlMicroService}/aws-customer-portal-search-service/_changelog.html`,
    },
  };
}

export const micro_services = getMicroServicesConfig();
